import { isAxiosError } from 'axios'
import { apiAxios } from '@/api'
import type { BankDetail } from './bank-detail.types'

/** Mirrors `SeparationGroupDetail` in the API's `transactions_to_separate.mapper.ts`. */
export interface SeparationAllowedAnswer {
  value: string
  label: string
  caption: string
  noteRequired: boolean
}

interface SeparationRow {
  transactionIds: number[]
  dateDisplay: string
  description: string | null
  details?: BankDetail[]
  amountDisplay: string
}

export interface SeparationGroup {
  groupKey: string
  kind: 'sort' | 'money_movement'
  groupName: string
  logoUrl: string | null
  accounts: string[]
  accountCount: number
  rows: SeparationRow[]
  moreRowCount: number
  amountDisplay: string
  transactionCount: number
  reason: string | null
  suggestion: string | null
  allowedAnswers: SeparationAllowedAnswer[]
  rowAnswers: { value: string; label: string }[]
  groupsRemaining: number
  groupKeys: string[]
  groupsNotShown: number
  maxQuestions: number
}

export interface SeparationRowPick {
  transactionIds: number[]
  value: string
}

export type SeparationSubmission =
  | { answer: string; note?: string }
  | { rows: { transactionIds: number[]; answer: string }[] }

export interface SeparationBatch {
  groupKeys: string[]
  groupsNotShown: number
  maxQuestions: number
}

export interface SeparationSaved extends SeparationBatch {
  recordedCount: number
  alreadySettled: boolean
  groupsRemaining: number
  finished: boolean
}

export const UNSURE_VALUE = 'unsure'

const GROUP_KEY = /^[0-9a-f]{16}$/

const GROUPS_PATH = '/finances/api/v1/transactions/separation/groups'

const groupPath = (groupKey: string): string => `${GROUPS_PATH}/${groupKey}`

const isGroupKey = (value: unknown): value is string =>
  typeof value === 'string' && GROUP_KEY.test(value)

/** Only keys the server could have issued. Calls saved in older conversations carry one `groupKey`. */
export const toGroupKeys = (input: Record<string, unknown>): string[] => {
  if (Array.isArray(input.groupKeys)) return input.groupKeys.filter(isGroupKey)
  return isGroupKey(input.groupKey) ? [input.groupKey] : []
}

const isNotFound = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 404

export const fetchSeparationBatch = async (): Promise<SeparationBatch> => {
  const { data } = await apiAxios.get<SeparationBatch>(GROUPS_PATH)
  return data
}

export const finishSeparationGroups = async (): Promise<void> => {
  await apiAxios.post(`${GROUPS_PATH}/finish`)
}

/** Null when the group is gone, which means it was answered somewhere else. */
export const fetchSeparationGroup = async (
  groupKey: string
): Promise<SeparationGroup | null> => {
  try {
    const { data } = await apiAxios.get<SeparationGroup>(groupPath(groupKey))
    return data
  } catch (error) {
    if (isNotFound(error)) return null
    throw error
  }
}

/** Reports each key whose group is gone; a failed check reports nothing. */
export const reportGoneGroups = (
  groupKeys: string[],
  onGone: (groupKey: string) => void
): void => {
  groupKeys.forEach((groupKey) => {
    fetchSeparationGroup(groupKey)
      .then((loaded) => {
        if (!loaded) onGone(groupKey)
      })
      .catch(() => undefined)
  })
}

/** Null when the group was answered somewhere else since the card loaded it. */
export const saveSeparationAnswer = async (
  groupKey: string,
  submission: SeparationSubmission
): Promise<SeparationSaved | null> => {
  try {
    const { data } = await apiAxios.post<SeparationSaved>(
      `${groupPath(groupKey)}/answer`,
      submission
    )
    return data
  } catch (error) {
    if (isNotFound(error)) return null
    throw error
  }
}
